import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ModalDialog.scss';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ModalDialogProps {
  /** Controls visibility. Returns null when false so portals are not mounted. */
  open: boolean;
  /** Primary heading shown in the header. */
  title: string;
  /** Optional muted line rendered below the title. */
  subtitle?: string;
  /** Called when the × button, backdrop, or Escape key is pressed. */
  onClose: () => void;
  /**
   * When provided the body + footer are wrapped in a <form> whose onSubmit
   * fires this handler (and calls e.preventDefault() automatically).
   */
  onSubmit?: (e: React.FormEvent) => void;

  // ── Sizing ──────────────────────────────────────────────────────────────────
  /** Default 840. Accepts a number (→ px) or any valid CSS length string. */
  width?: string | number;
  /** Default '96vw'. */
  maxWidth?: string | number;
  /** Default '75vw'. */
  minWidth?: string | number;
  /** Default '60vh'. */
  minHeight?: string | number;
  /** Default '90vh'. */
  maxHeight?: string | number;

  // ── Slots ───────────────────────────────────────────────────────────────────
  /** Scrollable body content. */
  children: React.ReactNode;
  /**
   * When 'tabs': body padding is removed and overflow is hidden so a tabbed
   * layout can control its own scrolling. Default: 'scroll'.
   */
  bodyVariant?: 'scroll' | 'tabs';
  /** Actions shown on the LEFT side of the footer (e.g. delete / secondary). */
  footerLeft?: React.ReactNode;
  /** Actions shown on the RIGHT side of the footer (e.g. save / close). */
  footerRight?: React.ReactNode;

  className?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function toCss(value: string | number | undefined, fallback: string): string {
  if (value === undefined) return fallback;
  return typeof value === 'number' ? `${value}px` : value;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ModalDialog: React.FC<ModalDialogProps> = ({
  open,
  title,
  subtitle,
  onClose,
  onSubmit,
  width = 840,
  maxWidth = '96vw',
  minWidth = '75vw',
  minHeight = '60vh',
  maxHeight = '90vh',
  children,
  bodyVariant = 'scroll',
  footerLeft,
  footerRight,
  className,
}) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const containerStyle: React.CSSProperties = {
    width:     toCss(width,     '840px'),
    maxWidth:  toCss(maxWidth,  '96vw'),
    minWidth:  toCss(minWidth,  '75vw'),
    minHeight: toCss(minHeight, '60vh'),
    maxHeight: toCss(maxHeight, '90vh'),
  };

  const hasFooter = !!(footerLeft || footerRight);

  const footer = hasFooter && (
    <div className="app-modal__footer">
      {footerLeft && (
        <div className="app-modal__footer-left">
          <div className="app-modal__footer-actions">{footerLeft}</div>
        </div>
      )}
      <div className="app-modal__footer-right">{footerRight}</div>
    </div>
  );

  const body = (
    <div className={`app-modal__body${bodyVariant === 'tabs' ? ' app-modal__body--tabs' : ''}`}>
      {children}
    </div>
  );

  const inner = onSubmit ? (
    <form onSubmit={e => { e.preventDefault(); onSubmit(e); }}>
      {body}
      {footer}
    </form>
  ) : (
    <>{body}{footer}</>
  );

  const dialog = (
    <div className="app-modal-backdrop" onClick={onClose}>
      <div
        className={`app-modal${className ? ` ${className}` : ''}`}
        style={containerStyle}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="app-modal__header">
          <div>
            <h4 className="app-modal__title">{title}</h4>
            {subtitle && <span className="app-modal__subtitle">{subtitle}</span>}
          </div>
          <button
            type="button"
            className="app-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {inner}
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default ModalDialog;

